import { MICROSOFT365_READ_TOOLS, type McpImportDto, type McpServerDto, type McpTestResultDto, type McpToolDto, type WorkspaceDto } from '@dbrain/shared';
import { Alert, Badge, Button, ConfirmDialog, Dialog, EmptyState, Field, Icons, Menu, MenuItem, MenuSeparator, Select, Skeleton, Switch, TextArea, TextInput } from '@dbrain/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { api } from '../api/client.js';
import { capKeys, useLibraryServers, useWebPolicy } from '../api/capabilities.js';
import { useCompanyZone, useDashboard } from '../api/hooks.js';
import { useMicrosoft365Status } from '../api/microsoft365.js';
import { ErrorAlert } from '../components/ErrorAlert.js';
import { LibrarySection, PlaceSelect, ScopeChips, ScopeTag, type LibraryScope } from '../components/library/Library.js';
import { ConnectionsUpsell, OwnLimitCount, OwnLimitNote, useOwnLimit } from '../components/plan/PlanParts.js';
import { useOrganizationAdmin, usePlan } from '../api/plan.js';
import { useCapabilities } from '../experience.js';
import { Microsoft365Card } from '../components/microsoft365/Microsoft365Card.js';
import { Microsoft365Result } from '../components/microsoft365/Result.js';
import { cardView, summaryShare } from '../components/microsoft365/state.js';
import { PageHeader } from '../components/PageHeader.js';
import { formatCount, humanTime } from '@dbrain/app-core/format';

/**
 * Connected services (MCP; 06 §14.2, ADR-0014; SA-039…SA-046, QW-1101…QW-1116; BaglantilarYeni, D-118):
 * the organization's (Herkes), a team's (a domain's shared workspace), one's own (Kişisel) and other
 * workspaces' servers in sections, two cards a row: the service, its state in words, its switch, the first
 * tools; the rarer actions in "…", the service's notes and the whole tool list under "Ayrıntılar". Keys and
 * tokens go to the server side and are never shown again; a configuration is tested (its tools listed)
 * before it is saved; tools can be allowed, asked or turned off one by one. Before them all, full width, the
 * built-in connections ("Hazır bağlantılar", Phase 15e): Microsoft 365, each person with their own account.
 */
type Transport = 'http' | 'sse' | 'stdio';
type AuthKind = 'none' | 'headers' | 'oauth';
type Filter = 'all' | 'builtin' | 'organization' | 'team' | 'user' | 'workspace';
/** "Hazır" (the built-in connections, Phase 15e) right after "Tümü" */
const CHIPS: Filter[] = ['all', 'builtin', 'organization', 'team', 'user', 'workspace'];
/** how many tool names a card shows before "+N araç" */
const TOOL_PREVIEW = 4;

/** Where a connection lives on the page: Herkes, a team (a domain's shared workspace), Kişisel or a workspace. */
function placeOf(server: McpServerDto, workspaces: Map<string, WorkspaceDto>): { scope: LibraryScope; name: string | null } {
  if (server.scope === 'tenant') return { scope: 'organization', name: null };
  if (server.scope === 'user') return { scope: 'user', name: null };
  const workspace = server.workspaceId ? workspaces.get(server.workspaceId) : undefined;
  return { scope: workspace?.directoryManaged ? 'team' : 'workspace', name: workspace?.name ?? null };
}

/** The person has to connect their own account (a sign-in, or keys of their own) before the tools work. */
function needsAccount(server: McpServerDto): boolean {
  if (server.authKind === 'oauth') return !server.connection?.signedIn;
  const personal = (server.transport === 'stdio' ? server.envNames.length > 0 : server.authKind === 'headers') && !server.sharedCredentials;
  return personal && !server.connection?.hasCredentials;
}

/** Hazır / Hesabınızı bağlayın / Ulaşılamıyor, or what else keeps it from working */
function statusOf(server: McpServerDto, t: (key: string) => string): { tone: 'ready' | 'account' | 'down' | 'off'; text: string } {
  if (server.usable) return { tone: 'ready', text: t('connectors.state.ready') };
  if (needsAccount(server)) return { tone: 'account', text: t('connectors.state.account') };
  if (server.blockedReason === 'error' || server.blockedReason === 'not_discovered') return { tone: 'down', text: t('connectors.state.down') };
  return { tone: 'off', text: t(`connectors.blocked.${server.blockedReason ?? 'disabled'}`) };
}

/** A host on the company's own network ("Şirket içi") or out on the internet ("Uzak sunucu"). */
function locationOf(server: McpServerDto): 'local' | 'internal' | 'remote' {
  if (server.transport === 'stdio') return 'local';
  let host = '';
  try {
    host = new URL(server.url ?? '').hostname.toLowerCase();
  } catch {
    return 'remote';
  }
  const internal =
    !host.includes('.') ||
    /\.(local|internal|intranet|corp|lan|home\.arpa)$/.test(host) ||
    /^(10\.|127\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(host) ||
    host === 'localhost';
  return internal ? 'internal' : 'remote';
}

const TILE_TONES = ['blue', 'teal', 'pink', 'amber', 'purple'] as const;
function toneOfName(name: string): (typeof TILE_TONES)[number] {
  let hash = 0;
  for (const char of name) hash = (hash * 31 + (char.codePointAt(0) ?? 0)) >>> 0;
  return TILE_TONES[hash % TILE_TONES.length] ?? 'blue';
}

function ToolList({ server, workspaceId }: { server: McpServerDto; workspaceId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const policy = useMutation({
    mutationFn: (input: { tool: McpToolDto; policy: string }) =>
      api('PUT', `/api/mcp/servers/${server.id}/tools/${encodeURIComponent(input.tool.name)}/policy${workspaceId ? `?workspaceId=${workspaceId}` : ''}`, { policy: input.policy }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: capKeys.serversAll }),
  });
  if (server.tools.length === 0) return <p className="muted">{t('connectors.noTools')}</p>;
  return (
    <ul className="connector-tools" data-testid="connector-tools">
      {server.tools.map((tool) => (
        <li key={tool.name} className="row">
          <code>{tool.name}</code>
          {tool.readOnly ? <Badge>{t('connectors.readOnly')}</Badge> : null}
          <span className="muted connector-tools__description">{tool.title ?? tool.description ?? ''}</span>
          {server.canManage ? (
            <Select
              value={tool.policy}
              aria-label={t('connectors.policyFor', { tool: tool.name })}
              onChange={(e) => policy.mutate({ tool, policy: e.target.value })}
              data-testid="connector-tool-policy"
            >
              <option value="ask">{t('connectors.policy.ask')}</option>
              <option value="allow">{t('connectors.policy.allow')}</option>
              <option value="deny">{t('connectors.policy.deny')}</option>
            </Select>
          ) : (
            <Badge>{t(`connectors.policy.${tool.policy}`)}</Badge>
          )}
        </li>
      ))}
      <ErrorAlert error={policy.error} />
    </ul>
  );
}

function Credentials({ server, workspaceId, onDone }: { server: McpServerDto; workspaceId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const names = server.transport === 'stdio' ? server.envNames : server.headerNames;
  const [values, setValues] = useState<Record<string, string>>(Object.fromEntries(names.map((n) => [n, ''])));
  const save = useMutation({
    mutationFn: () =>
      api('PUT', `/api/mcp/servers/${server.id}/credentials${workspaceId ? `?workspaceId=${workspaceId}` : ''}`, {
        ...(server.transport === 'stdio' ? { env: values } : { headers: values }),
        shared: server.sharedCredentials,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: capKeys.serversAll });
      onDone();
    },
  });
  return (
    <form
      className="connector-credentials"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <p className="muted">{server.sharedCredentials ? t('connectors.credentials.shared') : t('connectors.credentials.personal')}</p>
      {names.map((name) => (
        <Field key={name} label={name}>
          {({ id }) => <TextInput id={id} type="password" autoComplete="off" value={values[name] ?? ''} onChange={(e) => setValues((v) => ({ ...v, [name]: e.target.value }))} data-testid="connector-credential" />}
        </Field>
      ))}
      <ErrorAlert error={save.error} />
      <div className="row">
        <Button size="sm" type="submit" busy={save.isPending} disabled={names.some((n) => !values[n])}>
          {t('common.save')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}

/**
 * `own` (16c.2, B2cBaglantiSiniri): one of the person's own connections on a package with a limit, listed under
 * "Kendi bağlantılarınız": a plug tile instead of the initial, no place tags (they are all the person's own) and a
 * line saying Oppy asks before important actions.
 */
function ServerCard({ server, workspaceId, place, admin, own = false }: { server: McpServerDto; workspaceId: string; place: { scope: LibraryScope; name: string | null }; admin: boolean; own?: boolean }) {
  const { t, i18n } = useTranslation();
  const zone = useCompanyZone();
  const queryClient = useQueryClient();
  const [keys, setKeys] = useState(false);
  const [details, setDetails] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: capKeys.serversAll });
  // a workspace's own server is looked after in that workspace
  const home = workspaceId || (server.scope === 'workspace' ? (server.workspaceId ?? '') : '');
  const where = home ? `?workspaceId=${home}` : '';
  const act = useMutation({
    mutationFn: (request: { method: 'POST' | 'PUT' | 'DELETE'; path: string; body?: unknown }) => api(request.method, request.path, request.body),
    onSuccess: (_data, request) => {
      if (request.method === 'DELETE' && !request.path.endsWith('/credentials')) setDeleting(false);
      refresh();
    },
  });
  const signIn = useMutation({
    mutationFn: () => api<{ authorizationUrl: string } | { connected: true }>('POST', `/api/mcp/servers/${server.id}/oauth/start`, { returnTo: '/connectors' }),
    onSuccess: (result) => {
      if ('authorizationUrl' in result) window.location.assign(result.authorizationUrl);
      else refresh();
    },
  });
  const status = statusOf(server, t);
  const account = needsAccount(server);
  const location = locationOf(server);
  const keyed = server.authKind === 'headers' || (server.transport === 'stdio' && server.envNames.length > 0);
  const shown = server.tools.slice(0, TOOL_PREVIEW);
  const connect = () => (server.authKind === 'oauth' ? signIn.mutate() : setKeys(true));
  return (
    <li className={own ? 'connector-card connector-card--own' : 'connector-card'} data-testid="connector-row" data-name={server.name} data-status={status.tone}>
      <div className="connector-card__head">
        {own ? (
          <span className="connector-initial connector-initial--own" aria-hidden="true">
            <Icons.plug size={19} />
          </span>
        ) : (
          <span className={`connector-initial connector-initial--${toneOfName(server.displayName)}`} aria-hidden="true">
            {server.displayName.charAt(0).toLocaleUpperCase(i18n.language)}
          </span>
        )}
        <div className="connector-card__title">
          <h3>{server.displayName}</h3>
          <code className="connector-card__address">{server.transport === 'stdio' ? [server.command, ...server.args].join(' ') : server.url}</code>
        </div>
        <span className={`connector-state connector-state--${status.tone}`} data-testid="connector-state">
          {status.text}
        </span>
        <Switch
          checked={server.enabled}
          disabled={server.enabledScope === 'required' || act.isPending}
          onChange={(e) => act.mutate({ method: 'PUT', path: `/api/mcp/servers/${server.id}/enabled`, body: { enabled: e.target.checked, ...(workspaceId ? { workspaceId } : {}) } })}
          data-testid="connector-toggle"
          aria-label={t('connectors.toggle', { name: server.displayName })}
        />
        <Menu align="right" label={t('connectors.actions', { name: server.displayName })} triggerClassName="ui-icon-button ui-icon-button--sm" trigger={<Icons.more size={16} />} data-testid="connector-menu">
          {keyed ? (
            <MenuItem icon={<Icons.key />} onClick={() => setKeys(true)} data-testid="connector-keys">
              {t('connectors.enterKeys')}
            </MenuItem>
          ) : null}
          {server.authKind === 'oauth' && server.connection?.signedIn ? (
            <MenuItem icon={<Icons.regenerate />} onClick={() => signIn.mutate()} data-testid="connector-signin-again">
              {t('connectors.signInAgain')}
            </MenuItem>
          ) : null}
          <MenuItem icon={<Icons.regenerate />} onClick={() => act.mutate({ method: 'POST', path: `/api/mcp/servers/${server.id}/discover${where}` })} data-testid="connector-refresh">
            {t('connectors.refresh')}
          </MenuItem>
          {server.transport === 'stdio' && home ? (
            <MenuItem icon={<Icons.regenerate />} onClick={() => act.mutate({ method: 'POST', path: `/api/mcp/servers/${server.id}/restart${where}` })}>
              {t('connectors.restart')}
            </MenuItem>
          ) : null}
          {admin && server.scope === 'tenant' ? (
            <MenuItem
              icon={<Icons.check />}
              hint={t('connectors.trustHint')}
              onClick={() => act.mutate({ method: 'PUT', path: `/api/mcp/servers/${server.id}/trust`, body: { trusted: !server.trusted } })}
              data-testid="connector-trust"
            >
              {server.trusted ? t('connectors.untrust') : t('connectors.trust')}
            </MenuItem>
          ) : null}
          {server.connection?.hasCredentials ? (
            <MenuItem icon={<Icons.key />} onClick={() => act.mutate({ method: 'DELETE', path: `/api/mcp/servers/${server.id}/credentials` })} data-testid="connector-forget-keys">
              {t('connectors.forgetKeys')}
            </MenuItem>
          ) : null}
          {server.canManage ? (
            <>
              <MenuSeparator />
              <MenuItem danger icon={<Icons.trash />} onClick={() => setDeleting(true)} data-testid="connector-delete">
                {t('common.delete')}
              </MenuItem>
            </>
          ) : null}
        </Menu>
      </div>
      {server.tools.length > 0 ? (
        <p className="connector-tools-preview" data-testid="connector-tools-preview">
          {shown.map((tool) => (
            <code key={tool.name} className="connector-tool">
              {tool.name}
            </code>
          ))}
          {server.tools.length > TOOL_PREVIEW ? <span className="connector-tools-preview__more">{t('connectors.moreTools', { count: server.tools.length - TOOL_PREVIEW })}</span> : null}
        </p>
      ) : (
        <p className="connector-card__note">{account ? t('connectors.toolsAfterSignIn') : t('connectors.noTools')}</p>
      )}
      {server.blockedReason === 'not_approved' ? (
        <div className="row">
          <Button size="sm" onClick={() => act.mutate({ method: 'POST', path: `/api/mcp/servers/${server.id}/approve${where}` })} data-testid="connector-approve">
            {t('connectors.approve')}
          </Button>
        </div>
      ) : null}
      {keys ? <Credentials server={server} workspaceId={home} onDone={() => setKeys(false)} /> : null}
      <div className="connector-card__foot">
        {own ? (
          <p className="connector-card__ask">{t('b2cModels.connections.ask')}</p>
        ) : (
          <>
            <ScopeTag scope={place.scope} name={place.name} />
            <span className="scope-tag">{t(`connectors.location.${location}`)}</span>
          </>
        )}
        {server.catalogMode === 'required' ? <span className="scope-tag mode-tag--required">{t('connectors.required')}</span> : null}
        {server.trusted ? (
          <span className="scope-tag" title={t('connectors.trustHint')}>
            {t('connectors.trusted')}
          </span>
        ) : null}
        <span className="connector-card__used">
          {server.connection?.lastUsedAt
            ? t('connectors.lastUsed', { date: humanTime(server.connection.lastUsedAt, i18n.language, t, true, zone) })
            : account && server.authKind === 'oauth'
              ? t('connectors.eachOwnAccount')
              : null}
        </span>
        {account && !keys ? (
          <Button size="sm" busy={signIn.isPending} onClick={connect} data-testid="connector-signin">
            {t('connectors.connectAccount')}
          </Button>
        ) : null}
        <button type="button" className="link-button connector-card__details" aria-expanded={details} onClick={() => setDetails((v) => !v)} data-testid="connector-details">
          {t('connectors.details')}
        </button>
      </div>
      {details ? (
        <div className="connector-card__more" data-testid="connector-more">
          {server.description ? <p className="connector-card__description">{server.description}</p> : null}
          {server.connection?.lastError && !server.usable ? <Alert tone="warning" title={t('connectors.lastError')} details={server.connection.lastError} detailsLabel={t('common.technicalDetails')} /> : null}
          {server.instructions ? (
            <>
              <h4>{t('connectors.instructions')}</h4>
              <p className="muted connector-card__notes">{server.instructions}</p>
            </>
          ) : null}
          <h4>{t('connectors.tools', { count: server.tools.length })}</h4>
          <ToolList server={server} workspaceId={home} />
        </div>
      ) : null}
      <ErrorAlert error={deleting ? null : (act.error ?? signIn.error)} />
      {deleting ? (
        <ConfirmDialog
          title={t('connectors.deleteTitle', { name: server.displayName })}
          confirmLabel={t('common.delete')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          busy={act.isPending}
          error={<ErrorAlert error={act.error} />}
          onConfirm={() => act.mutate({ method: 'DELETE', path: `/api/mcp/servers/${server.id}${where}` })}
          onClose={() => {
            setDeleting(false);
            act.reset();
          }}
        >
          {t('connectors.confirmDelete', { name: server.displayName })}
        </ConfirmDialog>
      ) : null}
    </li>
  );
}

interface Draft {
  scope: 'user' | 'workspace' | 'tenant';
  name: string;
  displayName: string;
  description: string;
  transport: Transport;
  url: string;
  command: string;
  args: string;
  authKind: AuthKind;
  oauthScope: string;
  pairs: { name: string; value: string }[];
  catalogMode: 'available' | 'default_on' | 'required';
  sharedCredentials: boolean;
}

const emptyDraft: Draft = {
  scope: 'user',
  name: '',
  displayName: '',
  description: '',
  transport: 'http',
  url: '',
  command: '',
  args: '',
  authKind: 'none',
  oauthScope: '',
  pairs: [],
  catalogMode: 'available',
  sharedCredentials: false,
};

const splitArgs = (text: string) => text.split('\n').map((s) => s.trim()).filter(Boolean);

function AddServer({ onClose, workspaceId, admin }: { onClose: () => void; workspaceId: string; admin: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dashboard = useDashboard();
  const codeSpaces = (dashboard.data?.workspaces ?? []).filter((w) => w.kind === 'code');
  const [draft, setDraft] = useState<Draft>({ ...emptyDraft, ...(workspaceId ? { scope: 'workspace' as const } : {}) });
  const [space, setSpace] = useState(workspaceId || codeSpaces[0]?.id || '');
  const [json, setJson] = useState('');
  const [tested, setTested] = useState<McpTestResultDto | null>(null);
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setTested(null);
  };
  const values = Object.fromEntries(draft.pairs.filter((p) => p.name).map((p) => [p.name, p.value]));
  const config = {
    transport: draft.transport,
    ...(draft.transport === 'stdio' ? { command: draft.command.trim(), args: splitArgs(draft.args) } : { url: draft.url.trim() }),
    authKind: draft.transport === 'stdio' ? 'none' : draft.authKind,
    ...(draft.authKind === 'oauth' && draft.oauthScope ? { oauthScope: draft.oauthScope } : {}),
    ...(draft.transport === 'stdio' ? { envNames: Object.keys(values) } : { headerNames: draft.authKind === 'headers' ? Object.keys(values) : [] }),
  };
  const test = useMutation({
    mutationFn: () =>
      api<McpTestResultDto>('POST', '/api/mcp/test', {
        ...config,
        ...(draft.transport === 'stdio' ? { env: values, workspaceId: space } : { headers: draft.authKind === 'headers' ? values : {} }),
      }),
    onSuccess: setTested,
  });
  const importJson = useMutation({
    mutationFn: () => api<McpImportDto[]>('POST', '/api/mcp/import', { json }),
    onSuccess: (list) => {
      const first = list[0];
      if (!first) return;
      setDraft((d) => ({
        ...d,
        name: first.name ?? d.name,
        displayName: d.displayName || first.name || '',
        transport: first.config.transport,
        url: first.config.url ?? '',
        command: first.config.command ?? '',
        args: (first.config.args ?? []).join('\n'),
        authKind: first.config.authKind,
        pairs: Object.entries(first.config.transport === 'stdio' ? first.env : first.headers).map(([name, value]) => ({ name, value })),
      }));
    },
  });
  const save = useMutation({
    mutationFn: () =>
      api<McpServerDto>('POST', '/api/mcp/servers', {
        ...config,
        scope: draft.scope,
        ...(draft.scope === 'workspace' ? { workspaceId: space } : {}),
        name: draft.name.trim(),
        displayName: draft.displayName.trim() || draft.name.trim(),
        ...(draft.description.trim() ? { description: draft.description.trim() } : {}),
        ...(draft.scope === 'tenant' ? { catalogMode: draft.catalogMode, sharedCredentials: draft.sharedCredentials } : {}),
        ...(Object.keys(values).length > 0 && (draft.transport === 'stdio' || draft.authKind === 'headers')
          ? { credentials: draft.transport === 'stdio' ? { env: values } : { headers: values } }
          : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: capKeys.serversAll });
      onClose();
    },
  });
  const needsPairs = draft.transport === 'stdio' || draft.authKind === 'headers';
  return (
    <Dialog title={t('connectors.add.title')} closeLabel={t('common.close')} onClose={onClose} size="wide" data-testid="connector-add-dialog">
      <p className="muted">{t('connectors.add.hint')}</p>
      <div className="row">
        <Field label={t('connectors.add.where')}>
          {({ id }) => (
            <Select id={id} value={draft.scope} onChange={(e) => set('scope', e.target.value as Draft['scope'])} data-testid="connector-scope">
              <option value="user">{t('connectors.scopes.user')}</option>
              {codeSpaces.length > 0 ? <option value="workspace">{t('connectors.scopes.workspace')}</option> : null}
              {admin ? <option value="tenant">{t('connectors.scopes.tenant')}</option> : null}
            </Select>
          )}
        </Field>
        {draft.scope === 'workspace' || draft.transport === 'stdio' ? (
          <Field label={t('connectors.add.workspace')}>
            {({ id }) => (
              <Select id={id} value={space} onChange={(e) => setSpace(e.target.value)}>
                {codeSpaces.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : null}
        <Field label={t('connectors.add.kind')}>
          {({ id }) => (
            <Select id={id} value={draft.transport} onChange={(e) => set('transport', e.target.value as Transport)} data-testid="connector-transport">
              <option value="http">{t('connectors.add.http')}</option>
              <option value="sse">{t('connectors.add.sse')}</option>
              <option value="stdio">{t('connectors.add.stdio')}</option>
            </Select>
          )}
        </Field>
      </div>
      <div className="row">
        <Field label={t('connectors.add.name')} hint={t('connectors.add.nameHint')}>
          {({ id }) => <TextInput id={id} value={draft.name} onChange={(e) => set('name', e.target.value.toLowerCase())} data-testid="connector-name" />}
        </Field>
        <Field label={t('connectors.add.displayName')}>
          {({ id }) => <TextInput id={id} value={draft.displayName} onChange={(e) => set('displayName', e.target.value)} data-testid="connector-display-name" />}
        </Field>
      </div>
      <Field label={t('connectors.add.description')}>
        {({ id }) => <TextInput id={id} value={draft.description} onChange={(e) => set('description', e.target.value)} />}
      </Field>
      {draft.transport === 'stdio' ? (
        <>
          <Alert tone="info" title={t('connectors.add.stdioHint')} />
          <Field label={t('connectors.add.command')}>
            {({ id }) => <TextInput id={id} value={draft.command} onChange={(e) => set('command', e.target.value)} placeholder="npx" data-testid="connector-command" />}
          </Field>
          <Field label={t('connectors.add.args')} hint={t('connectors.add.argsHint')}>
            {({ id }) => <TextArea id={id} rows={3} value={draft.args} onChange={(e) => set('args', e.target.value)} data-testid="connector-args" />}
          </Field>
        </>
      ) : (
        <>
          <Field label={t('connectors.add.url')}>
            {({ id }) => <TextInput id={id} value={draft.url} onChange={(e) => set('url', e.target.value)} placeholder="https://…/mcp" data-testid="connector-url" />}
          </Field>
          <Field label={t('connectors.add.auth')}>
            {({ id }) => (
              <Select id={id} value={draft.authKind} onChange={(e) => set('authKind', e.target.value as AuthKind)} data-testid="connector-auth">
                <option value="none">{t('connectors.add.authNone')}</option>
                <option value="oauth">{t('connectors.add.authOauth')}</option>
                <option value="headers">{t('connectors.add.authHeaders')}</option>
              </Select>
            )}
          </Field>
          {draft.authKind === 'oauth' ? (
            <Field label={t('connectors.add.oauthScope')} hint={t('connectors.add.oauthHint')}>
              {({ id }) => <TextInput id={id} value={draft.oauthScope} onChange={(e) => set('oauthScope', e.target.value)} />}
            </Field>
          ) : null}
        </>
      )}
      {needsPairs ? (
        <fieldset className="connector-pairs">
          <legend>{draft.transport === 'stdio' ? t('connectors.add.env') : t('connectors.add.headers')}</legend>
          {draft.pairs.map((pair, index) => (
            <div key={index} className="row">
              <TextInput
                value={pair.name}
                placeholder={draft.transport === 'stdio' ? 'API_KEY' : 'Authorization'}
                aria-label={t('connectors.add.pairName')}
                onChange={(e) => set('pairs', draft.pairs.map((p, i) => (i === index ? { ...p, name: e.target.value } : p)))}
                data-testid="connector-pair-name"
              />
              <TextInput
                type="password"
                autoComplete="off"
                value={pair.value}
                aria-label={t('connectors.add.pairValue')}
                onChange={(e) => set('pairs', draft.pairs.map((p, i) => (i === index ? { ...p, value: e.target.value } : p)))}
                data-testid="connector-pair-value"
              />
              <Button size="sm" variant="ghost" onClick={() => set('pairs', draft.pairs.filter((_p, i) => i !== index))}>
                {t('common.delete')}
              </Button>
            </div>
          ))}
          <Button size="sm" variant="secondary" onClick={() => set('pairs', [...draft.pairs, { name: '', value: '' }])} data-testid="connector-pair-add">
            {t('connectors.add.addPair')}
          </Button>
          <p className="muted">{t('connectors.add.secretsHint')}</p>
        </fieldset>
      ) : null}
      {draft.scope === 'tenant' ? (
        <div className="row">
          <Field label={t('connectors.add.catalog')}>
            {({ id }) => (
              <Select id={id} value={draft.catalogMode} onChange={(e) => set('catalogMode', e.target.value as Draft['catalogMode'])}>
                <option value="available">{t('skills.catalog.available')}</option>
                <option value="default_on">{t('skills.catalog.default_on')}</option>
                <option value="required">{t('skills.catalog.required')}</option>
              </Select>
            )}
          </Field>
          {needsPairs ? (
            <label className="switch-row">
              <input type="checkbox" checked={draft.sharedCredentials} onChange={(e) => set('sharedCredentials', e.target.checked)} />
              <span>{t('connectors.add.shared')}</span>
            </label>
          ) : null}
        </div>
      ) : null}
      <details>
        <summary>{t('connectors.add.import')}</summary>
        <TextArea rows={4} value={json} onChange={(e) => setJson(e.target.value)} placeholder='{"mcpServers": {"name": {"url": "https://…"}}}' aria-label={t('connectors.add.import')} />
        <Button size="sm" variant="secondary" busy={importJson.isPending} disabled={!json.trim()} onClick={() => importJson.mutate()}>
          {t('connectors.add.importButton')}
        </Button>
        <ErrorAlert error={importJson.error} />
      </details>
      {tested ? (
        tested.ok ? (
          <Alert tone="success" title={t('connectors.add.testOk', { count: tested.tools.length })} data-testid="connector-test-ok">
            <ul className="connector-tools">
              {tested.tools.map((tool) => (
                <li key={tool.name}>
                  <code>{tool.name}</code> <span className="muted">{tool.title ?? tool.description ?? ''}</span>
                </li>
              ))}
            </ul>
          </Alert>
        ) : (
          <Alert tone="error" title={t('connectors.add.testFailed')} details={tested.message} detailsLabel={t('common.technicalDetails')} data-testid="connector-test-failed" />
        )
      ) : null}
      <ErrorAlert error={test.error ?? save.error} />
      <div className="row dialog__actions">
        <Button variant="ghost" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button variant="secondary" busy={test.isPending} disabled={draft.authKind === 'oauth' && draft.transport !== 'stdio'} onClick={() => test.mutate()} data-testid="connector-test">
          {t('connectors.add.test')}
        </Button>
        <Button busy={save.isPending} disabled={!draft.name.trim()} onClick={() => save.mutate()} data-testid="connector-save">
          {t('common.save')}
        </Button>
      </div>
      {draft.authKind === 'oauth' ? <p className="muted">{t('connectors.add.oauthAfterSave')}</p> : null}
    </Dialog>
  );
}

interface Group {
  key: string;
  title: string;
  hint: string;
  scope: LibraryScope;
  servers: McpServerDto[];
}

export function ConnectorsPage() {
  const { t, i18n } = useTranslation();
  const dashboard = useDashboard();
  const policy = useWebPolicy();
  const [params, setParams] = useSearchParams();
  const admin = useOrganizationAdmin();
  // D-268: own connections are on the person's plan (B2cBaglantilar); kept ones stay listed, "Pro'da"
  const plan = usePlan();
  const planLocked = plan !== null && !plan.ownConnections;
  const ownConnections = useOwnLimit('connections');
  // 16c.2 (B2cBaglantiSiniri): a package with a number of own connections lists them alone, "n / limit"; at 0 the
  // B2cBaglantilar card stays (planLocked)
  const limited = plan !== null && !planLocked && ownConnections !== null && ownConnections.limit !== null && ownConnections.limit > 0 ? ownConnections : null;
  // Phase 16b: a person's own account says nothing of a company's systems (B2cBaglantilar)
  const personal = !useCapabilities().organization;
  const [workspaceId, setWorkspaceId] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const servers = useLibraryServers(workspaceId);
  const workspaceList = dashboard.data?.workspaces ?? [];
  const workspaces = new Map(workspaceList.map((w) => [w.id, w]));
  const all = servers.data ?? [];
  const placed = all.map((server) => ({ server, place: placeOf(server, workspaces) }));
  const words = query.trim().toLocaleLowerCase(i18n.language);
  const matches = placed.filter(({ server }) => !words || `${server.displayName} ${server.name} ${server.url ?? ''} ${server.tools.map((tool) => tool.name).join(' ')}`.toLocaleLowerCase(i18n.language).includes(words));
  // the library pages' order: each team, Herkes, Kişisel, Çalışma alanları
  const teams = [...new Set(matches.filter((m) => m.place.scope === 'team').map((m) => m.place.name ?? ''))].sort((a, b) => a.localeCompare(b, i18n.language));
  const groups: Group[] = [
    ...teams.map((team) => ({
      key: `team-${team}`,
      title: t('connectors.sections.teamTitle', { team }),
      hint: t('connectors.sections.team'),
      scope: 'team' as const,
      servers: matches.filter((m) => m.place.scope === 'team' && (m.place.name ?? '') === team).map((m) => m.server),
    })),
    ...(['organization', 'user', 'workspace'] as const).map((scope) => ({
      key: scope,
      title: t(`library.scopes.${scope}`),
      hint: t(`connectors.sections.${scope}`),
      scope,
      servers: matches.filter((m) => m.place.scope === scope).map((m) => m.server),
    })),
  ];
  const shown = groups.filter((g) => filter === 'all' || g.scope === filter);
  const count = (scope: LibraryScope) => placed.filter((p) => p.place.scope === scope).length;
  const on = all.filter((s) => s.enabled);
  const tools = all.filter((s) => s.enabled && s.usable).reduce((sum, s) => sum + s.tools.length, 0);
  const oauthResult = params.get('mcp');
  // the built-in Microsoft 365 (Phase 15e): first and full width; a member does not see it before the firm sets it
  // up, nobody in a personal space; connected and on, it counts in the summary with the tools the firm has on
  const microsoft365 = useMicrosoft365Status().data;
  const m365Unavailable = params.get('microsoft365') === 'error' && params.get('reason') === 'unavailable';
  const builtin = cardView(microsoft365, m365Unavailable);
  const builtinWords = [t('microsoft365.user.name'), 'outlook', t('microsoft365.user.products.mail'), t('microsoft365.user.products.calendar'), ...MICROSOFT365_READ_TOOLS.flatMap((tool) => [tool, t(`microsoft365.user.tools.${tool}.title`)])]
    .join(' ')
    .toLocaleLowerCase(i18n.language);
  const showBuiltin = microsoft365 !== undefined && builtin.kind !== 'hidden' && (filter === 'all' || filter === 'builtin') && (!words || builtinWords.includes(words));
  const share = summaryShare(microsoft365);
  // Phase 16b (B2cBaglantilar): with no connection of their own (on Ücretsiz always) a person sees the card alone, no
  // place filter, search or count; the card says where keys are kept
  const bare = plan !== null && (planLocked || (servers.data !== undefined && all.length === 0));
  const m365Connected = () =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('microsoft365', 'connected');
        next.delete('reason');
        return next;
      },
      { replace: true },
    );
  return (
    <div className="page page--wide connectors-page">
      <PageHeader
        title={t('connectors.title')}
        intro={
          <>
            {t(personal ? 'connectors.introPersonal' : 'connectors.introShort')}
            {bare || limited ? null : (
              <span className="connectors-page__safe">
                <Icons.lock size={12} />
                {t('connectors.safe')}
              </span>
            )}
          </>
        }
        actions={
          planLocked ? undefined : (
            <span className="own-limit__actions">
              {/* 16c.1: a package with a count of own connections says "n / limit"; at the limit adding waits */}
              {ownConnections && ownConnections.limit !== null ? <OwnLimitCount kind="connections" used={ownConnections.used} limit={ownConnections.limit} /> : null}
              <Button
                size="lg"
                className={ownConnections?.full === true ? 'own-limit__locked' : undefined}
                icon={ownConnections?.full === true ? <Icons.lock size={16} /> : <Icons.plus size={18} />}
                onClick={() => setAdding(true)}
                disabled={(!admin && policy.data?.mcpUserServers === false) || ownConnections?.full === true}
                title={ownConnections?.full === true ? t('b2cModels.connections.locked') : undefined}
                data-testid="connector-add"
              >
                {t('connectors.add.button')}
              </Button>
            </span>
          )
        }
      />
      {!planLocked && ownConnections?.full && ownConnections.limit !== null ? <OwnLimitNote kind="connections" limit={ownConnections.limit} /> : null}
      {oauthResult ? (
        <Alert
          tone={oauthResult === 'connected' ? 'success' : 'error'}
          title={oauthResult === 'connected' ? t('connectors.oauth.connected') : t('connectors.oauth.failed')}
          action={
            <Button size="sm" variant="ghost" onClick={() => setParams({})}>
              {t('common.close')}
            </Button>
          }
          data-testid="connector-oauth-result"
        >
          {params.get('reason') ?? null}
        </Alert>
      ) : null}
      <Microsoft365Result />
      {!admin && policy.data?.mcpUserServers === false ? <Alert tone="info" title={t('connectors.userServersOff')} /> : null}
      {bare || limited ? null : (
        <div className="library-toolbar">
          <ScopeChips
            label={t('library.filter')}
            testId="connectors-scope"
            active={filter}
            onChange={setFilter}
            items={CHIPS.filter((key) => key !== 'builtin' || (microsoft365 !== undefined && microsoft365.state !== 'unsupported')).map((key) =>
              key === 'all'
                ? { key, label: t('library.scopes.all') }
                : key === 'builtin'
                  ? { key, label: t('microsoft365.user.connectors.chip'), count: builtin.kind === 'hidden' ? 0 : 1 }
                  : { key, label: t(`library.scopes.${key}`), count: count(key) },
            )}
          />
          <div className="library-toolbar__end">
            <PlaceSelect value={workspaceId} onChange={setWorkspaceId} workspaces={workspaceList.map((w) => ({ id: w.id, name: w.name }))} hint={t('connectors.placeHint')} testId="connectors-place" />
            <label className="search-pill">
              <Icons.search size={16} />
              <span className="ui-visually-hidden">{t('connectors.search')}</span>
              <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('connectors.search')} data-testid="connectors-search" />
            </label>
          </div>
        </div>
      )}
      {servers.data && !bare && !limited ? (
        <p className="library-summary" data-testid="connectors-summary">
          <span>
            {t('connectors.summaryNow')} <strong>{t('connectors.summaryOn', { count: on.length + share.on })}</strong>
          </span>
          <span aria-hidden="true"> · </span>
          <span>{t('connectors.summaryTools', { count: tools + share.tools, tools: formatCount(tools + share.tools, i18n.language) })}</span>
        </p>
      ) : null}
      <ErrorAlert error={servers.error} />
      {showBuiltin && microsoft365 ? (
        <LibrarySection
          title={t('microsoft365.user.connectors.section')}
          hint={t('microsoft365.user.connectors.sectionHint')}
          count={t('connectors.sections.count', { count: 1 })}
          testId="connectors-builtin"
        >
          <ul className="connector-grid connector-grid--single">
            <Microsoft365Card status={microsoft365} workspaceId={workspaceId} unavailable={m365Unavailable} onConnected={m365Connected} />
          </ul>
        </LibrarySection>
      ) : null}
      {servers.isPending ? <Skeleton block /> : null}
      {planLocked && servers.data ? <ConnectionsUpsell kept={all.length} /> : null}
      {limited && servers.data ? (
        <section className="own-connections" aria-labelledby="own-connections-title" data-testid="own-connections">
          <div className="own-connections__head">
            <h2 id="own-connections-title" className="own-connections__title">
              {t('b2cModels.connections.section')}
            </h2>
            <span className="own-connections__count" data-testid="own-connections-section-count">
              · {t('b2cModels.connections.count', { used: formatCount(limited.used, i18n.language), limit: formatCount(limited.limit ?? 0, i18n.language) })}
            </span>
          </div>
          {all.length === 0 ? (
            <p className="own-connections__empty" data-testid="own-connections-empty">
              {t('b2cModels.connections.empty')}
            </p>
          ) : (
            <ul className="connector-grid connector-grid--single">
              {all.map((server) => (
                <ServerCard key={server.id} server={server} workspaceId={workspaceId} place={placeOf(server, workspaces)} admin={admin} own />
              ))}
            </ul>
          )}
        </section>
      ) : null}
      {servers.data && !planLocked && !limited && !showBuiltin && shown.every((g) => g.servers.length === 0) ? (
        <EmptyState
          icon={<Icons.connections />}
          title={filter === 'all' ? t('connectors.emptyAll') : filter === 'builtin' ? t('microsoft365.user.connectors.empty') : t(`connectors.emptyScope.${filter}`)}
        />
      ) : null}
      {(limited ? [] : shown)
        .filter((g) => g.servers.length > 0)
        .map((group) => (
          <LibrarySection key={group.key} title={group.title} hint={group.hint} count={t('connectors.sections.count', { count: group.servers.length })} testId={`connectors-${group.key}`}>
            <ul className="connector-grid">
              {group.servers.map((server) => (
                <ServerCard key={server.id} server={server} workspaceId={workspaceId} place={placeOf(server, workspaces)} admin={admin} />
              ))}
            </ul>
          </LibrarySection>
        ))}
      {adding ? <AddServer onClose={() => setAdding(false)} workspaceId={workspaceId} admin={admin} /> : null}
    </div>
  );
}
